import type { Config } from '../../types/config';
import type { GetMediaParams } from '../../types/media';
import type { PlaylistItem, Source } from '../../types/playlist';
import { MANIFEST_TYPE } from '../constants';
import env from '../env';

const isDashSource = (source: Source) => source.type === MANIFEST_TYPE.dash;
const isHlsSource = (source: Source) => source.type === MANIFEST_TYPE.hls || source.type === 'application/x-mpegurl';

// Puts the source matching the streaming protocol first, with the other manifest type as fallback;
// the remaining sources are kept (e.g. for casting, which requires DASH)
const orderSources = (sources: Source[], streamingProtocol: 'dash' | 'hls') => {
  const preferred = streamingProtocol === 'dash' ? sources.find(isDashSource) : sources.find(isHlsSource);
  const alternative = streamingProtocol === 'dash' ? sources.find(isHlsSource) : sources.find(isDashSource);
  const source = preferred ?? alternative ?? sources[0];

  return source ? [source, ...sources.filter((current) => current !== source)] : sources;
};

export const processSources = (item: PlaylistItem, config: Config, userId?: string, streamingProtocol?: 'dash' | 'hls'): PlaylistItem => {
  const baseUrl = env.APP_CONTENT_API_BASE_URL;
  const { adConfig, siteId, adDeliveryMethod } = config;
  const hasServerAds = !!adConfig && adDeliveryMethod === 'ssai';

  const sources = streamingProtocol ? orderSources(item.sources, streamingProtocol) : item.sources;

  const processedSources = sources.map((source: Source) => {
    const url = new URL(source.file);

    const isDRM = (url.searchParams.has('exp') && url.searchParams.has('sig')) || source.drm;

    // Use SSAI URL for configs with server side ads, DRM is not supported yet
    if (hasServerAds && !isDRM) {
      url.href = `${baseUrl}/v2/sites/${siteId}/media/${item.mediaid}/ssai.${isDashSource(source) ? 'mpd' : 'm3u8'}`;
      url.searchParams.set('ad_config_id', adConfig);
    }

    // Attach user_id for VOD and BCL SaaS Live Streams
    if (userId) {
      url.searchParams.set('user_id', userId);
    }

    return { ...source, file: url.toString() };
  });

  return { ...item, sources: processedSources };
};

export default abstract class EntitlementService {
  readonly playbackRetryCount: number = 2;

  abstract getMediaToken(config: Config, mediaId: string, jwt?: string, params?: GetMediaParams, accessType?: string, offerIds?: string[]): Promise<string>;

  async startSession(
    item: PlaylistItem,
    signedItem?: PlaylistItem,
    startTime?: number,
    streamingProtocol?: 'dash' | 'hls',
    config?: Config,
    userId?: string,
  ): Promise<{ playableItem?: PlaylistItem; startOffset: number }> {
    const playableItem = signedItem ?? item;
    const startOffset = startTime ?? 0;

    if (!config) return { playableItem, startOffset };

    return { playableItem: processSources(playableItem, config, userId, streamingProtocol), startOffset };
  }

  async stopSession(_positionInMs: number): Promise<void> {}
}
